// Protocolo WebSocket (docs/02-arquitetura.md §5).
// SINCRONIZADO com internal/ws/protocol.go — protocol_sync_test.go lê este
// arquivo e compara as listas de tipos.

export const PROTOCOL_VERSION = 1;

// ---- fases, modos, vereditos ---------------------------------------------

export const PHASES = [
  "lobby",
  "intro",
  "answering",
  "judging",
  "review",
  "reveal",
  "leaderboard",
  "podium",
  "ended",
] as const;
export type Phase = (typeof PHASES)[number];

export const QUESTION_MODES = [
  "free_text",
  "multiple_choice",
  "true_false",
  "number",
  "poll",
  "rubric",
] as const;
export type QuestionMode = (typeof QUESTION_MODES)[number];

export const VERDICTS = [
  "correct",
  "partial",
  "wrong",
  "review",
  "manipulation",
  "pending",
  "void",
] as const;
export type Verdict = (typeof VERDICTS)[number];

export const VERDICT_SOURCES = [
  "exact",
  "fuzzy",
  "numeric",
  "jev",
  "host",
  "policy",
  "correction",
] as const;
export type VerdictSource = (typeof VERDICT_SOURCES)[number];

export const ROLES = ["player", "host", "stage"] as const;
export type Role = (typeof ROLES)[number];

// ---- cliente → servidor ----------------------------------------------------

export const CLIENT_TYPES = [
  "hello",
  "ping",
  "join",
  "answer",
  "react",
  "choose_team",
  "host.start",
  "host.next",
  "host.end_answering",
  "host.pause",
  "host.resume",
  "host.end",
  "host.lock",
  "host.review",
  "host.accept_suggestions",
  "host.kick",
] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

// ---- servidor → cliente ----------------------------------------------------

export const SERVER_TYPES = [
  "state",
  "phase",
  "player.joined",
  "player.left",
  "player.kicked",
  "answer.ack",
  "answer.count",
  "review.list",
  "reveal",
  "leaderboard",
  "podium",
  "reaction",
  "error",
  "kicked",
  "pong",
] as const;
export type ServerType = (typeof SERVER_TYPES)[number];

// ---- envelope ---------------------------------------------------------------

export interface Envelope<T = unknown> {
  t: ServerType | ClientType;
  seq?: number;
  d: T;
}

// ---- tipos de dados ----------------------------------------------------------

export interface Avatar {
  shape: number; // 0..7
  color: number; // 0..7 (índice --p1..--p8)
}

export interface Player {
  id: string;
  nickname: string;
  avatar: Avatar;
  team?: number;
  score?: number;
}

export interface MCOption {
  text: string;
  correct?: boolean; // só chega ao cliente após reveal (ou ao host)
}

export interface RubricLevel {
  what: string;
  examples?: string[];
}

// Pergunta como o cliente a vê (sem gabarito até reveal).
export interface QuestionView {
  index: number;
  mode: QuestionMode;
  prompt: string;
  imageUrl?: string;
  hint?: string; // só após reveal
  timeLimitS: number;
  points: number;
  options?: MCOption[];
  multiSelect?: boolean;
  tolerance?: number | string;
  unit?: string;
  levels?: RubricLevel[];
}

export interface AnswerKeyView {
  answer?: string | number | boolean;
  accepted?: string[];
  correctOptions?: number[];
  levels?: RubricLevel[];
  pointsPerLevel?: number[];
}

export interface AnswerView {
  questionIndex: number;
  raw: string;
  verdict?: Verdict;
  points?: number;
}

export interface ReviewItem {
  answerId: string;
  playerId: string;
  raw: string;
  suggestion: Verdict;
  prob?: number;
  confidence?: number;
  rubricLvl?: number;
}

export interface LeaderboardEntry {
  playerId: string;
  nickname: string;
  avatar: Avatar;
  team?: number;
  score: number;
  delta: number;
  rank: number;
}

export interface TeamScore {
  team: number;
  score: number;
  members: number;
}

// ---- payloads servidor → cliente ---------------------------------------------

export interface StateData {
  protocol: number;
  room: string;
  phase: Phase;
  questionIndex: number;
  totalQuestions: number;
  deadline?: number; // epoch ms
  serverTime: number;
  locked: boolean;
  paused: boolean;
  question?: QuestionView;
  players?: Player[]; // lobby / host
  answered?: number; // n/N
  you?: {
    playerId: string;
    nickname: string;
    avatar: Avatar;
    score: number;
    rank: number;
    streak: number;
    team?: number;
    answer?: AnswerView;
    verdict?: Verdict;
  };
  review?: { items: ReviewItem[]; deadline?: number; judgeUnavailable?: boolean };
  settings?: RoomSettingsView;
}

export interface RoomSettingsView {
  name?: string;
  teams?: number; // 0 = off
  speedScoring?: boolean;
  streak?: boolean;
  allowEdit?: boolean;
  showCommonAnswers?: boolean;
  autoAdvance?: boolean;
  classMode?: boolean;
  roster?: string[]; // modo turma (nomes ainda disponíveis)
}

export interface PhaseData {
  phase: Phase;
  questionIndex: number;
  deadline?: number;
  question?: QuestionView;
  reviewDeadline?: number;
  paused?: boolean;
}

export interface PlayerEventData {
  player: Player;
}

export interface AnswerAckData {
  questionIndex: number;
  accepted: boolean;
  editable: boolean;
  reason?: string;
}

export interface AnswerCountData {
  answered: number;
  total: number;
}

export interface ReviewListData {
  items: ReviewItem[];
  deadline?: number;
  judgeUnavailable?: boolean;
}

export interface RevealData {
  questionIndex: number;
  answerKey?: AnswerKeyView;
  distribution?: { optionIndex: number; count: number }[];
  closest?: { playerId: string; nickname?: string; value: number; distance: number }[];
  commonAnswers?: { text: string; count: number }[];
  manipulationCount?: number;
  correctCount: number;
  total: number;
  you?: { verdict: Verdict; points: number; rank: number; delta: number; rubricLevel?: number };
}

export interface LeaderboardData {
  top: LeaderboardEntry[];
  teams?: TeamScore[];
  you?: { rank: number; delta: number; score: number };
}

export interface PodiumData {
  first?: Player;
  second?: Player;
  third?: Player;
  teams?: TeamScore[];
  you?: { rank: number; score: number };
}

export interface ReactionData {
  emoji: string;
  count: number;
}

export interface ErrorData {
  code: string;
  message: string;
}

export interface KickedData {
  reason: string;
}

export interface PongData {
  serverTime: number;
  clientTime: number;
}

// ---- payloads cliente → servidor ----------------------------------------------

export interface HelloData {
  protocol: number;
  lastSeq?: number;
  clientTime: number;
}

export interface PingData {
  clientTime: number;
}

export interface JoinData {
  nickname: string;
  team?: number;
}

export interface AnswerData {
  questionIndex: number;
  value: string;
}

export interface ReactData {
  emoji: string;
}

export interface ChooseTeamData {
  team: number;
}

export interface HostLockData {
  locked: boolean;
}

export interface HostReviewData {
  answerId: string;
  verdict: "correct" | "partial" | "wrong";
}

export interface HostKickData {
  playerId: string;
  ban: boolean;
}
